"use client";

import { type UseQueryExecute, useQuery } from "@urql/next";
import { graphql } from "gql.tada";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { ChatQuotaStatusSnapshot } from "@/lib/ai/chat-quota-status";

const ChatQuotaStatusQuery = graphql(`
  query ChatQuotaStatus($organizationID: ID!) {
    low: aiQuotaStatus(organizationID: $organizationID, tier: LOW) {
      isExceeded
      quotaLimit
      remainingTokens
      isUnlimited
    }
    high: aiQuotaStatus(organizationID: $organizationID, tier: HIGH) {
      isExceeded
      quotaLimit
      remainingTokens
      isUnlimited
    }
  }
`);

type ChatQuotaStatusContextValue = {
  lowQuotaStatus: ChatQuotaStatusSnapshot;
  highQuotaStatus: ChatQuotaStatusSnapshot;
  reexecuteQuotaStatusQuery: UseQueryExecute;
};

const ChatQuotaStatusContext =
  createContext<ChatQuotaStatusContextValue | null>(null);

type ChatQuotaStatusProviderProps = {
  children: ReactNode;
  workspaceId: string;
  shouldFetchStatus?: boolean;
};

function toQuotaStatusSnapshot(
  status:
    | {
        isExceeded: boolean;
        quotaLimit: number;
        remainingTokens: number;
        isUnlimited: boolean;
      }
    | null
    | undefined,
): ChatQuotaStatusSnapshot {
  return {
    isLoaded: Boolean(status),
    isExceeded: Boolean(status?.isExceeded),
    quotaLimit:
      typeof status?.quotaLimit === "number" ? status.quotaLimit : null,
    remainingTokens:
      typeof status?.remainingTokens === "number"
        ? status.remainingTokens
        : null,
    isUnlimited: Boolean(status?.isUnlimited),
  };
}

/**
 * workspace 単位の quota query を一度だけ購読し、page と side panel の thread へ共有する。
 *
 * chatId 遷移中に旧・新 `ChatThread` が並行描画されても、URQL の query owner を
 * 増やさず render 中の相互更新を防ぐ。
 */
export function ChatQuotaStatusProvider({
  children,
  workspaceId,
  shouldFetchStatus = true,
}: ChatQuotaStatusProviderProps) {
  const variables = useMemo(
    () => ({
      organizationID: workspaceId,
    }),
    [workspaceId],
  );
  const [{ data }, reexecuteQuotaStatusQuery] = useQuery({
    query: ChatQuotaStatusQuery,
    variables,
    pause: !workspaceId || !shouldFetchStatus,
    requestPolicy: "cache-and-network",
  });
  const value = useMemo<ChatQuotaStatusContextValue>(
    () => ({
      lowQuotaStatus: toQuotaStatusSnapshot(data?.low),
      highQuotaStatus: toQuotaStatusSnapshot(data?.high),
      reexecuteQuotaStatusQuery,
    }),
    [data, reexecuteQuotaStatusQuery],
  );

  return (
    <ChatQuotaStatusContext.Provider value={value}>
      {children}
    </ChatQuotaStatusContext.Provider>
  );
}

/** workspace 共有の quota snapshot と再取得関数を返す。 */
export function useSharedChatQuotaStatus(): ChatQuotaStatusContextValue {
  const context = useContext(ChatQuotaStatusContext);
  if (!context) {
    throw new Error(
      "useSharedChatQuotaStatus must be used within ChatQuotaStatusProvider",
    );
  }
  return context;
}
